/**
 * FacilityLens frontend API service
 */

import { CORE_URL } from "@/constants/api";
import { VITE_CORE_API_URL } from "@/constants/env";
import { auth } from "@/services/auth-service";

const BASE = `${VITE_CORE_API_URL}${CORE_URL}/facility-lens`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function getToken(): Promise<string> {
	const token = await auth?.currentUser?.getIdToken();
	if (!token) throw new Error("Authentication required");
	return token;
}

async function apiGet<T>(path: string): Promise<T> {
	const token = await getToken();
	const res = await fetch(`${BASE}${path}`, {
		method: "GET",
		headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
	});
	const json = await res.json();
	if (!json.success) throw new Error(json.message ?? "Request failed");
	return json.data;
}

async function apiPost<T>(path: string, body: unknown): Promise<T> {
	const token = await getToken();
	const res = await fetch(`${BASE}${path}`, {
		method: "POST",
		headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});
	const json = await res.json();
	if (!json.success) throw new Error(json.message ?? "Request failed");
	return json.data;
}

// ─── Types ───────────────────────────────────────────────────────────────────

export interface KnowledgeIssue {
	title: string;
	description: string;
	severity: "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
	solutionSummary: string;
}

export interface KnowledgeManual {
	title: string;
	url: string;
	source: string;
	description: string;
}

export interface AIIdentification {
	assetType: string;
	manufacturer: string | null;
	model: string | null;
	condition: string | null;
	confidence: number;
	visibleIssues?: string[];
	rawDetails?: string;
}

export interface FollowUpRequest {
	type: string;
	instruction: string;
	hint: string;
}

// ─── Symptom-Based Troubleshooting ──────────────────────────────────────────

export interface FollowUpQuestion {
	question: string;
	options: string[];
	/** Only display this question when a prior question was answered a specific way */
	showWhen?: { questionIndex: number; answer: string };
}

export interface LikelyCause {
	cause: string;
	selfFixable: boolean;
	fixSteps?: string;
	severity?: "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
	/** Answer combinations that point to this cause — used by frontend scoring */
	indicators?: { questionIndex: number; answers: string[] }[];
}

export interface SymptomCard {
	symptom: string;
	icon: string;
	followUpQuestions: FollowUpQuestion[];
	likelyCauses: LikelyCause[];
	relatedIssueTitle: string | null;
}

export interface FacilityLensKnowledge {
	id: number;
	organizationId: number | null;
	assetType: string;
	manufacturer: string | null;
	model: string | null;
	commonIssues: KnowledgeIssue[];
	manualResources: KnowledgeManual[];
	specifications: Record<string, unknown>;
	troubleshootingTips: string[];
	symptomCards: SymptomCard[];
	timesReferenced: number;
	createdAt: string;
	updatedAt: string;
}

export type ResolveTier = "HASH_MATCH" | "QUICK_TRIAGE_CACHE" | "FAST_ANALYSIS" | "DEEP_ANALYSIS";

export interface FacilityLensScan {
	id: number;
	organizationId: number;
	userId: number;
	locationId: number | null;
	assetId: number | null;
	workOrderId: number | null;
	knowledgeEntryId: number | null;
	primaryPhotoUrl: string;
	photoHash: string | null;
	additionalPhotos: { url: string; type: string; caption?: string }[];
	aiIdentification: AIIdentification | null;
	status: string;
	followUpRequest: FollowUpRequest | null;
	selectedIssues: KnowledgeIssue[];
	createdAt: string;
	updatedAt: string;
	knowledge?: FacilityLensKnowledge | null;
	resolvedTier?: ResolveTier;
}

export interface FacilityLensMessage {
	id: number;
	scanId: number;
	workOrderId: number | null;
	senderUserId: number;
	senderRole: string;
	messageType: string;
	content: string;
	structuredQuestions: { question: string; options?: string[]; answer?: string }[] | null;
	isRead: boolean;
	createdAt: string;
	senderName?: string;
}

// ─── Scan API ────────────────────────────────────────────────────────────────

export async function flCreateScan(photoUrl: string, locationId?: number, photoHash?: string): Promise<FacilityLensScan> {
	return apiPost<FacilityLensScan>("/scan", { photoUrl, locationId, photoHash });
}

export async function flGetScan(id: number): Promise<FacilityLensScan> {
	return apiGet<FacilityLensScan>(`/scan/${id}`);
}

export async function flListScans(limit = 20, offset = 0): Promise<{ scans: FacilityLensScan[]; total: number }> {
	return apiGet(`/scans?limit=${limit}&offset=${offset}`);
}

export async function flAddPhoto(scanId: number, photoUrl: string, type: string, caption?: string): Promise<FacilityLensScan> {
	return apiPost<FacilityLensScan>(`/scan/${scanId}/photo`, { photoUrl, type, caption });
}

export async function flReAnalyze(scanId: number): Promise<FacilityLensScan> {
	return apiPost<FacilityLensScan>(`/scan/${scanId}/analyze`, {});
}

export async function flLinkWorkOrder(scanId: number, workOrderId: number): Promise<FacilityLensScan> {
	return apiPost<FacilityLensScan>(`/scan/${scanId}/link-work-order`, { workOrderId });
}

export async function flCreateWorkOrder(scanId: number, data: {
	title: string;
	description?: string;
	locationId?: number;
	priority?: number;
	workType?: string;
	selectedIssueIndices?: number[];
}): Promise<{ scan: FacilityLensScan; workOrderId: number }> {
	return apiPost(`/scan/${scanId}/create-work-order`, data);
}

// ─── Asset Matching ──────────────────────────────────────────────────────────

export interface MatchedAsset {
	id: number;
	locationId: number;
	assetTag: string | null;
	serialNumber: string | null;
	modelNumber: string | null;
	condition: string | null;
	manufacturerName: string | null;
	assetTypeName: string | null;
	locationName: string | null;
}

export interface MatchAssetsResult {
	matchedAssets: MatchedAsset[];
	bestMatch: MatchedAsset | null;
}

export async function flMatchAssets(scanId: number): Promise<MatchAssetsResult> {
	return apiGet<MatchAssetsResult>(`/scan/${scanId}/match-assets`);
}

// ─── Messages ────────────────────────────────────────────────────────────────

export async function flGetMessages(scanId: number): Promise<FacilityLensMessage[]> {
	return apiGet<FacilityLensMessage[]>(`/scan/${scanId}/messages`);
}

export async function flSendMessage(scanId: number, content: string, senderRole: string, messageType = "TEXT"): Promise<FacilityLensMessage> {
	return apiPost<FacilityLensMessage>(`/scan/${scanId}/messages`, { content, senderRole, messageType });
}

export async function flSuggestQuestions(scanId: number): Promise<{ question: string; options?: string[] }[]> {
	return apiGet(`/scan/${scanId}/suggest-questions`);
}

// ─── Knowledge Base ──────────────────────────────────────────────────────────

export async function flGetKnowledge(id: number): Promise<FacilityLensKnowledge> {
	return apiGet<FacilityLensKnowledge>(`/knowledge/${id}`);
}

export async function flSearchKnowledge(assetType: string, manufacturer?: string): Promise<FacilityLensKnowledge[]> {
	const params = new URLSearchParams({ assetType });
	if (manufacturer) params.set("manufacturer", manufacturer);
	return apiGet<FacilityLensKnowledge[]>(`/knowledge/search?${params}`);
}

// ─── Work Order Integration ──────────────────────────────────────────────────

export async function flGetScansByWorkOrder(workOrderId: number): Promise<FacilityLensScan[]> {
	return apiGet<FacilityLensScan[]>(`/work-order/${workOrderId}/scans`);
}
